/**
 * Device filtering — pure, so the thing testers rely on to find a phone is covered by tests.
 *
 * Search matches across everything printed on the row *and* the fields that are not printed but are
 * how people refer to a phone: name, model, serial, machine, site, TikTok account. Words are matched
 * independently, so "pixel oleg" finds the Pixel on oleg without caring about field order.
 */

import type { Device } from '../../api/types'

export type Mode = 'all' | 'free' | 'mine'

export function haystack(d: Device): string {
  return [d.name, d.model, d.serial, d.device_id, d.hub_id, d.point, d.tiktok_account, d.os_version, d.display_name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

export function matchesQuery(d: Device, query: string): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return true
  const hay = haystack(d)
  return words.every((w) => hay.includes(w))
}

export function machinesOf(devices: Device[]): string[] {
  return [...new Set(devices.map((d) => d.hub_id).filter((h): h is string => Boolean(h)))].sort()
}

export function filterDevices(
  devices: Device[],
  opts: {
    query?: string; machine?: string; mode?: Mode; currentSessionId?: string | null
    /** '' every phone, 'none' the ones not bound to a direction, else a direction id */
    direction?: string
    /** a direction's name, so searching "драми" finds the dramas phones */
    directionName?: (id: string) => string
  },
): Device[] {
  const { query = '', machine = '', mode = 'all', currentSessionId = null, direction = '', directionName } = opts
  return devices.filter((d) => {
    const mine = Boolean(d.held_by_me || (d.session_id && d.session_id === currentSessionId))
    // An empty machine means every machine — cancelling the choice must not hide anything.
    if (machine && d.hub_id !== machine) return false
    if (mode === 'free' && d.status === 'busy' && !mine) return false
    if (mode === 'mine' && !mine) return false
    if (direction === 'none' && d.direction_id) return false
    if (direction && direction !== 'none' && d.direction_id !== direction) return false
    const words = d.direction_id && directionName ? ` ${directionName(d.direction_id)}` : ''
    return matchesQuery(words ? { ...d, display_name: `${d.display_name ?? ''}${words}` } : d, query)
  })
}

/**
 * **Taken phones first** (owner, 2026-10-06: "обраний телефон нехай переміщається у верх списку, щоб всі
 * бачили, що він зайнятий і не шукали його пів години"). Yours, then the ones somebody else holds, then the
 * free ones, then those not on the wire — and within each, the order the service gave. Stable, so a list
 * that is polled every few seconds does not reshuffle under the pointer.
 */
export function takenFirst(devices: Device[], currentSessionId: string | null = null): Device[] {
  const rank = (d: Device) => {
    const mine = Boolean(d.held_by_me || (d.session_id && d.session_id === currentSessionId))
    if (mine) return 0
    if (d.status === 'busy') return 1
    if (d.status === 'online') return 2
    return 3
  }
  return devices.map((d, i) => ({ d, i })).sort((a, b) => rank(a.d) - rank(b.d) || a.i - b.i).map((x) => x.d)
}

